import { useState, useCallback } from 'react';
import {
    type CriterionValue,
    type DelayUnit,
    type FlowStep,
    type BranchStep,
    type DelayStep,
    type SMSStep,
    type SMSContent,
    type EmailStep,
    type SplitStep
} from '@spike/automation-model';

export type { FlowStep, BranchStep, DelayStep, SMSStep, SMSContent, EmailStep, SplitStep, DelayUnit };

export type InsertableType = 'delay' | 'split' | 'sms' | 'email';

// ─── Shared defaults & formatters ──────────────────────────────────────────────
// Exported so the AI-generated automation mapper (Automations/AI/mapGeneratedAutomation.ts)
// can hydrate raw generated content into the same label/subtitle shape the manual
// builder produces, without re-deriving this formatting logic.

export const DEFAULT_UNSUBSCRIBE_MESSAGE = 'Reply STOP to unsubscribe';
// Empty means "use the marketplace's own sender": a literal placeholder here is
// indistinguishable from a value the user typed, so it never gets replaced.
export const DEFAULT_SENDER_NAME = '';
export const DEFAULT_SENDER_EMAIL = '';

/**
 * What EmailSidebar hands back. Kept as a named type because the sidebar's
 * shape and the stored EmailStep differ (`name` vs `emailName`), and the
 * previous inline Omit<> silently didn't match — so the email name never
 * persisted.
 */
export interface EmailStepUpdate {
    name: string;
    emailTemplateId?: number | null;
    subjectLine: string;
    previewText: string;
    senderName: string;
    senderEmail: string;
    useAsReplyTo: boolean;
    skipRecentlyEmailed: boolean;
}

export const uid = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

export const formatDelayLabel = (amount: number, unit: DelayUnit, extraMinutes?: number): string => {
    const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
    const plural = amount === 1 ? unitLabel.slice(0, -1) : unitLabel;
    let label = `Wait ${amount} ${plural}`;
    if (unit === 'hours' && extraMinutes && extraMinutes > 0) {
        label += ` ${extraMinutes} ${extraMinutes === 1 ? 'Minute' : 'Minutes'}`;
    }
    return label;
};

export const formatSmsSubtitle = (message: string): string =>
    message.trim() ? message.slice(0, 40) + (message.length > 40 ? '…' : '') : 'Setup SMS';

export const formatEmailSubtitle = (subjectLine: string): string => subjectLine.trim() || 'Setup Email';

const DELAY_HOURS: Record<DelayUnit, number> = {
    minutes: 1 / 60,
    hours: 1,
    days: 24,
    weeks: 168
};

export const delayToHours = (amount: number, unit: DelayUnit, extraMinutes = 0): number =>
    (Number(amount) || 0) * (DELAY_HOURS[unit] ?? 24) + (Number(extraMinutes) || 0) / 60;

/**
 * The day each step lands on, keyed by step id. Advances only once the
 * accumulated wait crosses a full 24 hours, so "wait 3 hours" then "wait 20
 * hours" is still day 0.
 *
 * Derived rather than stored: an offset saved on the step goes stale the moment
 * a delay before it changes. Split branches each start from the split's own
 * total, since only one of the two runs.
 */
export const computeDayOffsets = (steps: FlowStep[]): Map<string, number> => {
    const offsets = new Map<string, number>();

    const walk = (list: FlowStep[], hoursSoFar: number): void => {
        let hours = hoursSoFar;

        list.forEach(step => {
            offsets.set(step.id, Math.floor(hours / 24));

            if (step.type === 'delay') {
                const d = step as DelayStep;
                hours += delayToHours(d.amount, d.unit, d.extraMinutes);
                return;
            }

            if (step.type === 'split') {
                (step as SplitStep).branches.forEach(branch => walk(branch.steps as FlowStep[], hours));
            }
        });
    };

    walk(steps, 0);

    return offsets;
};

export const formatSplitSubtitle = (conditions: CriterionValue[]): string =>
    conditions.length === 0 ? 'Set condition' : `${conditions.length} condition${conditions.length > 1 ? 's' : ''}`;

// ─── Tree helpers (work across main flow + branch steps) ─────────────────────

/** Find any step by id, searching recursively inside SplitStep branches. */
export function findStepById(id: string, stepList: FlowStep[]): FlowStep | undefined {
    for (const s of stepList) {
        if (s.id === id) return s;
        if (s.type === 'split') {
            for (const branch of (s as SplitStep).branches) {
                const found = findStepById(id, branch.steps as FlowStep[]);
                if (found) return found;
            }
        }
    }
    return undefined;
}

/**
 * Map over every step in the tree (main flow + branch steps),
 * applying `updater` when the id matches.
 */
function mapStepById(stepList: FlowStep[], id: string, updater: (s: FlowStep) => FlowStep): FlowStep[] {
    return stepList.map(s => {
        if (s.id === id) return updater(s);
        if (s.type === 'split') {
            const split = s as SplitStep;
            return {
                ...split,
                branches: split.branches.map(branch => ({
                    ...branch,
                    steps: mapStepById(branch.steps as FlowStep[], id, updater) as BranchStep[]
                })) as SplitStep['branches']
            };
        }
        return s;
    });
}

/** Filter out a step by id from every level of the tree. */
function filterStepById(stepList: FlowStep[], id: string): FlowStep[] {
    return stepList
        .filter(s => s.id !== id)
        .map(s => {
            if (s.type !== 'split') return s;
            const split = s as SplitStep;
            return {
                ...split,
                branches: split.branches.map(branch => ({
                    ...branch,
                    steps: branch.steps.filter(bs => bs.id !== id)
                })) as SplitStep['branches']
            };
        });
}

export const countType = (steps: FlowStep[], type: InsertableType): number =>
    steps.reduce((acc, s) => {
        const self = s.type === type ? 1 : 0;
        const nested =
            s.type === 'split'
                ? (s as SplitStep).branches.reduce((bAcc, b) => bAcc + countType(b.steps as FlowStep[], type), 0)
                : 0;
        return acc + self + nested;
    }, 0);

function makeStep(type: InsertableType, steps: FlowStep[]): FlowStep {
    switch (type) {
        case 'delay':
            return {
                id: uid(),
                type: 'delay',
                label: 'Wait 3 Hours',
                subtitle: '',
                amount: 3,
                unit: 'hours',
                extraMinutes: 0
            };
        case 'split':
            return {
                id: uid(),
                type: 'split',
                label: 'Conditional Split',
                subtitle: 'Set condition',
                conditionLabel: '',
                conditions: [],
                branches: [
                    { side: 'yes', steps: [] },
                    { side: 'no', steps: [] }
                ]
            };
        case 'sms': {
            const n = countType(steps, 'sms') + 1;
            return {
                id: uid(),
                type: 'sms',
                label: `SMS #${n}`,
                subtitle: 'Setup SMS',
                dayOffset: 0,
                smsName: `SMS #${n}`,
                content: {
                    message: '',
                    unsubscribeMessage: DEFAULT_UNSUBSCRIBE_MESSAGE
                },
                skipCampaign: true,
                skipFlow: true
            };
        }
        case 'email': {
            const n = countType(steps, 'email') + 1;
            return {
                id: uid(),
                type: 'email',
                label: `Email #${n}`,
                subtitle: 'Setup Email',
                emailName: `Email #${n}`,
                subjectLine: '',
                previewText: '',
                senderName: DEFAULT_SENDER_NAME,
                senderEmail: DEFAULT_SENDER_EMAIL,
                useAsReplyTo: true,
                skipRecentlyEmailed: true,
                dayOffset: 0
            };
        }
    }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useAutomationFlow = (initialSteps?: FlowStep[]) => {
    const [steps, setSteps] = useState<FlowStep[]>(
        initialSteps ?? [{ id: 'trigger-1', type: 'trigger', label: 'Trigger', subtitle: 'Select a trigger' }]
    );

    // Insert into the MAIN flow after a given step; returns the new step's id.
    const insertAfterStep = useCallback((afterId: string, type: InsertableType): string => {
        const stableId = uid();
        setSteps(prev => {
            const idx = prev.findIndex(s => s.id === afterId);
            if (idx === -1) return prev;
            if (prev[idx].type === 'split') return prev; // split is terminal

            if (type === 'split') {
                // Move any steps that follow the insertion point into the yes branch
                const tailSteps = prev.slice(idx + 1) as BranchStep[];
                const newSplit: SplitStep = {
                    ...(makeStep('split', prev) as SplitStep),
                    id: stableId,
                    branches: [
                        { side: 'yes', steps: tailSteps },
                        { side: 'no', steps: [] }
                    ]
                };
                return [...prev.slice(0, idx + 1), newSplit];
            }

            const newStep = { ...makeStep(type, prev), id: stableId };
            const next = [...prev];
            next.splice(idx + 1, 0, newStep);
            return next;
        });
        return stableId;
    }, []);

    // Insert into a BRANCH (yes/no) of a split step — works for nested splits too
    const insertIntoBranch = useCallback(
        (splitId: string, branch: 'yes' | 'no', afterBranchStepId: string | null, type: InsertableType) => {
            setSteps(prev =>
                mapStepById(prev, splitId, s => {
                    if (s.type !== 'split') return s;
                    const split = s as SplitStep;
                    const branchIndex = branch === 'yes' ? 0 : 1;
                    const current = [...split.branches[branchIndex].steps];
                    const newStep = makeStep(type, prev) as BranchStep;

                    if (afterBranchStepId === null) {
                        current.push(newStep);
                    } else {
                        const idx = current.findIndex(bs => bs.id === afterBranchStepId);
                        current.splice(idx === -1 ? current.length : idx + 1, 0, newStep);
                    }

                    const newBranches: SplitStep['branches'] = [
                        branchIndex === 0 ? { side: 'yes', steps: current } : split.branches[0],
                        branchIndex === 1 ? { side: 'no', steps: current } : split.branches[1]
                    ];

                    return { ...split, branches: newBranches };
                })
            );
        },
        []
    );

    const removeStep = useCallback((id: string) => {
        setSteps(prev => filterStepById(prev, id));
    }, []);

    const updateTriggerStep = useCallback((subtitle: string, chips?: string[]) => {
        setSteps(prev => prev.map(s => (s.id === 'trigger-1' ? { ...s, subtitle, chips } : s)));
    }, []);

    const updateDelayStep = useCallback((id: string, amount: number, unit: DelayUnit, extraMinutes?: number) => {
        const label = formatDelayLabel(amount, unit, extraMinutes);
        setSteps(prev => mapStepById(prev, id, s => ({ ...s, label, amount, unit, extraMinutes })));
    }, []);

    const flipSplitStep = useCallback((id: string) => {
        setSteps(prev =>
            mapStepById(prev, id, s => {
                if (s.type !== 'split') return s;
                const split = s as SplitStep;
                return {
                    ...split,
                    branches: [
                        { ...split.branches[1], side: 'yes' as const },
                        { ...split.branches[0], side: 'no' as const }
                    ]
                };
            })
        );
    }, []);

    const updateSplitStep = useCallback((id: string, conditions: CriterionValue[]) => {
        const subtitle = formatSplitSubtitle(conditions);
        setSteps(prev =>
            mapStepById(prev, id, s =>
                s.type === 'split' ? { ...s, subtitle, conditionLabel: subtitle, conditions } : s
            )
        );
    }, []);

    const updateSMSStep = useCallback(
        (id: string, smsName: string, content: SMSContent, skipCampaign: boolean, skipFlow: boolean) => {
            const subtitle = formatSmsSubtitle(content.message);
            setSteps(prev =>
                mapStepById(prev, id, s =>
                    s.type === 'sms'
                        ? { ...s, label: smsName || s.label, subtitle, content, skipCampaign, skipFlow, smsName }
                        : s
                )
            );
        },
        []
    );

    const updateSMSStepContent = useCallback((id: string, content: SMSContent) => {
        setSteps(prev =>
            mapStepById(prev, id, s => {
                if (s.type !== 'sms') return s;
                return { ...s, content, subtitle: formatSmsSubtitle(content.message) } as SMSStep;
            })
        );
    }, []);

    const updateEmailStep = useCallback((id: string, config: EmailStepUpdate) => {
        const subtitle = formatEmailSubtitle(config.subjectLine);
        setSteps(prev =>
            mapStepById(prev, id, s =>
                s.type === 'email'
                    ? {
                          ...s,
                          label: config.name || s.label,
                          subtitle,
                          emailName: config.name,
                          emailTemplateId: config.emailTemplateId ?? null,
                          subjectLine: config.subjectLine,
                          previewText: config.previewText,
                          senderName: config.senderName,
                          senderEmail: config.senderEmail,
                          useAsReplyTo: config.useAsReplyTo,
                          skipRecentlyEmailed: config.skipRecentlyEmailed
                      }
                    : s
            )
        );
    }, []);

    return {
        steps,
        insertAfterStep,
        insertIntoBranch,
        removeStep,
        updateTriggerStep,
        updateDelayStep,
        flipSplitStep,
        updateSplitStep,
        updateSMSStep,
        updateSMSStepContent,
        updateEmailStep
    };
};
